/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMemo } from 'react'

import type { EuiComboBoxOptionOption } from '@elastic/eui'

import useGetBillingCloudResources from '@modules/billing-lib/queries/useGetBillingCloudResources'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import {
  CLOUD_RESOURCE_NAME,
  DEFAULT_CLOUD_RESOURCE_NAME,
} from '@/components/Billing/BillingNotifications/Budgets/constants'

import type { BudgetConfigurationValues } from '../../../../../types'

const SELECTED_OPTION_MAX_ID_CHARACTERS = 6

type useCloudResourceProps = {
  values: BudgetConfigurationValues
}

const useCloudResource = ({ values }: useCloudResourceProps) => {
  const organizationId = useOrganizationId()

  const {
    data: billingCloudResources,
    isLoading,
    isError: isLoadingError,
    refetch,
  } = useGetBillingCloudResources(organizationId)

  const { scopeValues } = values

  const sortByLabel = (a: { label: string }, b: { label: string }) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase())

  const cloudResourceOptions: EuiComboBoxOptionOption[] = useMemo(() => {
    if (!billingCloudResources) {
      return []
    }

    // Group resources by type using a Map
    const groupsMap = new Map<string, Array<{ key: string; label: string }>>()

    for (const { id, name, type } of billingCloudResources) {
      const groupLabel = CLOUD_RESOURCE_NAME[type] || DEFAULT_CLOUD_RESOURCE_NAME
      const options = groupsMap.get(groupLabel) || []
      options.push({ key: id, label: `${name} (${id})` })
      groupsMap.set(groupLabel, options)
    }

    // Convert Map to array, sort options within groups, then sort groups
    return Array.from(groupsMap.entries(), ([label, options]) => ({
      label,
      options: options.sort(sortByLabel),
    })).sort(sortByLabel)
  }, [billingCloudResources])

  const { selectedOptions, hasDeletedCloudResource } = useMemo(() => {
    if (!scopeValues || scopeValues.length === 0) {
      return { selectedOptions: [], hasDeletedCloudResource: false }
    }

    // Build map of available resources with formatted labels
    const availableResourcesMap = new Map(
      cloudResourceOptions.flatMap((group) =>
        (group.options || []).map((option) => {
          const { key, label } = option
          const resourceName = label.replace(/\s*\(.*?\)\s*$/, '')
          const lastIdCharacters = String(key).slice(-SELECTED_OPTION_MAX_ID_CHARACTERS)
          return [key, { key, label: `${resourceName} (${lastIdCharacters})` }]
        }),
      ),
    )

    // Map scope values to options, using placeholders for missing resources
    let hasDeletedResource = false
    const selection = scopeValues
      .map((scopeValue) => {
        const foundResource = availableResourcesMap.get(scopeValue)

        if (foundResource) {
          return foundResource
        }

        hasDeletedResource = true
        return { key: scopeValue, label: scopeValue }
      })
      .sort(sortByLabel)

    return {
      selectedOptions: selection,
      hasDeletedCloudResource: hasDeletedResource,
    }
  }, [scopeValues, cloudResourceOptions])

  return {
    cloudResourceOptions,
    hasDeletedCloudResource,
    isLoading,
    isLoadingError,
    refetch,
    selectedOptions,
  }
}

export default useCloudResource
